import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, invites as invitesApi, ROLE_LABELS, setAccessToken, type InviteDescription } from '../api';
import { useAuth } from '../auth';

export function AcceptInvite() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { me, login, logout, refreshMe } = useAuth();

  const [info, setInfo] = useState<InviteDescription | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    invitesApi
      .describe(token)
      .then(setInfo)
      .catch((e) => setLoadError(e instanceof ApiError ? e.message : 'This invite link is not valid'));
  }, [token]);

  if (loadError) return <Shell><p className="error">{loadError}</p></Shell>;
  if (!info) return <Shell><p className="muted">Loading…</p></Shell>;

  const rolesText = info.roles.map((r) => ROLE_LABELS[r]).join(', ');

  if (info.accepted) {
    return (
      <Shell>
        <h1>Already used</h1>
        <p className="muted">This invite to {info.unitName} has already been accepted.</p>
      </Shell>
    );
  }
  if (info.expired) {
    return (
      <Shell>
        <h1>Invite expired</h1>
        <p className="muted">Ask an admin of {info.unitName} to send a new one.</p>
      </Shell>
    );
  }

  // Signed in as the wrong person.
  if (me && me.user.email.toLowerCase() !== info.email.toLowerCase()) {
    return (
      <Shell>
        <h1>Wrong account</h1>
        <p className="muted">
          This invite is for <strong>{info.email}</strong>, but you’re signed in as {me.user.email}.
        </p>
        <button onClick={() => void logout()}>Sign out</button>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1>Join {info.unitName}</h1>
      <p className="muted">
        You’ve been invited as <strong>{rolesText}</strong> ({info.email}).
      </p>

      {me ? (
        <AcceptButton token={token} onDone={async () => { await refreshMe(); navigate('/'); }} />
      ) : info.accountExists ? (
        <SignInThenAccept
          email={info.email}
          onSignedIn={async (email, password) => {
            await login(email, password);
          }}
        />
      ) : (
        <CreateAccountAndAccept
          token={token}
          onDone={async (accessToken) => {
            setAccessToken(accessToken);
            await refreshMe();
            navigate('/');
          }}
        />
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="shell">
      <div className="card">{children}</div>
    </main>
  );
}

function AcceptButton({ token, onDone }: { token: string; onDone: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      {error && <p className="error">{error}</p>}
      <button
        className="primary"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            await invitesApi.accept(token, {});
            await onDone();
          } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Could not accept');
            setBusy(false);
          }
        }}
      >
        {busy ? '…' : 'Accept invite'}
      </button>
    </>
  );
}

function SignInThenAccept({
  email,
  onSignedIn,
}: {
  email: string;
  onSignedIn: (email: string, password: string) => Promise<void>;
}) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      onSubmit={async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await onSignedIn(email, password);
          // On success the parent re-renders with `me` set and shows Accept.
        } catch (err) {
          setError(err instanceof ApiError ? err.message : 'Sign in failed');
          setBusy(false);
        }
      }}
    >
      <p className="muted small">You already have an account — sign in to accept.</p>
      <label>
        Email
        <input value={email} readOnly />
      </label>
      <label>
        Password
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoFocus
        />
      </label>
      {error && <p className="error">{error}</p>}
      <button type="submit" className="primary" disabled={busy}>
        {busy ? '…' : 'Sign in & accept'}
      </button>
    </form>
  );
}

function CreateAccountAndAccept({
  token,
  onDone,
}: {
  token: string;
  onDone: (accessToken: string) => Promise<void>;
}) {
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      onSubmit={async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          const res = await invitesApi.accept(token, { password, displayName });
          if (!res.accessToken) throw new Error('no session');
          await onDone(res.accessToken);
        } catch (err) {
          setError(err instanceof ApiError ? err.message : 'Could not create your account');
          setBusy(false);
        }
      }}
    >
      <p className="muted small">Set up your account to join.</p>
      <label>
        Your name
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required autoFocus />
      </label>
      <label>
        Password
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={12}
        />
      </label>
      <p className="hint">At least 12 characters.</p>
      {error && <p className="error">{error}</p>}
      <button type="submit" className="primary" disabled={busy}>
        {busy ? '…' : 'Create account & join'}
      </button>
    </form>
  );
}
